import type {
  VoyageEvaluationSummary,
  VoyagePerformanceReport,
} from "../models/voyage-evaluation.models";
import {
  formatVoyageEvaluationDate,
  formatVoyageEvaluationNumber,
  isSafeVoyageEvaluationLink,
} from "../utils/voyage-evaluation-selectors.utils";

interface Props {
  voyage: VoyageEvaluationSummary;
  reports: VoyagePerformanceReport[];
}

const average = (values: Array<number | null>): number | null => {
  const populated = values.filter((value): value is number => value !== null && Number.isFinite(value));

  return populated.length ? populated.reduce((total, value) => total + value, 0) / populated.length : null;
};

const SUMMARY_HEADINGS = [
  "Reports",
  "Condition",
  "Voy",
  "Referece Date",
  "Avg calculated STW",
  "Avg ME cons / 24h",
  "Avg draft",
  "Avg AUX cons / 24h",
  "Avg SOG",
  "Avg total cons / 24h",
  "Draft Corr Speed",
  "Normalised ECO Cons",
];

const getPerformanceSummaryRows = (reports: VoyagePerformanceReport[]): string[][] => {
  const reportsByCondition = new Map<string, VoyagePerformanceReport[]>();
  reports.forEach((report) => {
    const condition = report.condition ?? "—";
    reportsByCondition.set(condition, [...(reportsByCondition.get(condition) ?? []), report]);
  });

  return Array.from(reportsByCondition.entries()).map(([condition, conditionReports]) => {
    const reportDates = conditionReports
      .map((report) => report.reportDate)
      .filter((reportDate): reportDate is string => Boolean(reportDate))
      .sort();
    const voyageNumbers = conditionReports.map((report) => Number(report.voyage)).filter(Number.isFinite);

    return [
      String(conditionReports.length),
      condition,
      voyageNumbers.length ? String(Math.min(...voyageNumbers)) : "—",
      reportDates.length ? formatVoyageEvaluationDate(reportDates[reportDates.length - 1]) : "—",
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.calculatedStw)), 2),
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.totalMeConsumption24h)), 2),
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.draft)), 2),
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.totalAuxConsumption24h)), 2),
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.sog)), 2),
      formatVoyageEvaluationNumber(average(conditionReports.map((report) => report.totalConsumption24h)), 2),
      "Unavailable",
      "Unavailable",
    ];
  });
};

// Groups the filtered semantic rows by condition, as the Power BI summary table does.
// A detail-row selection supplies one row here and therefore cross-filters both tables.
const VoyagePerformanceSummary = ({ voyage, reports }: Props) => {
  const summaryRows = getPerformanceSummaryRows(reports);

  return (
    <div className="grid gap-4" data-test="voyage-performance-summary">
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700"><h2 className="text-sm font-semibold">Actual Performance Table</h2></div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 text-xs">
          <dt className="text-gray-500">IMO</dt><dd className="font-semibold">{voyage.imo}</dd>
          <dt className="text-gray-500">Vessel Name</dt><dd className="font-semibold">{voyage.vesselName}</dd>
          <dt className="text-gray-500">Vessel Type</dt><dd>{voyage.vesselType ?? "—"}</dd>
          <dt className="text-gray-500">Contract Type</dt><dd>{voyage.contractType ?? "—"}</dd>
          <dt className="text-gray-500">OPR Type</dt><dd>{voyage.oprType ?? "—"}</dd>
          <dt className="text-gray-500">Voyage</dt><dd>{voyage.voyage}</dd>
          <dt className="text-gray-500">ZN Link</dt><dd>{isSafeVoyageEvaluationLink(voyage.znLink) ? <a href={voyage.znLink!} target="_blank" rel="noreferrer" className="font-semibold text-green underline">Open</a> : "—"}</dd>
          <dt className="text-gray-500">IMOS Link</dt><dd>{isSafeVoyageEvaluationLink(voyage.imosLink) ? <a href={voyage.imosLink!} target="_blank" rel="noreferrer" className="font-semibold text-green underline">Open</a> : "—"}</dd>
        </dl>
      </section>
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700"><h2 className="text-sm font-semibold">Voyage Performance Summary</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px] text-[11px]">
            <thead className="bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900"><tr>{SUMMARY_HEADINGS.map((heading) => <th key={heading} className="whitespace-nowrap px-3 py-3 font-semibold">{heading}</th>)}</tr></thead>
            <tbody>{summaryRows.map((row) => <tr key={row[1]}>{row.map((value, index) => <td key={SUMMARY_HEADINGS[index]} className="whitespace-nowrap border-t border-gray-200 px-3 py-3 font-medium dark:border-gray-700">{value}</td>)}</tr>)}</tbody>
          </table>
          {!summaryRows.length && <p className="p-6 text-center text-xs text-gray-500">No performance summary rows.</p>}
        </div>
      </section>
    </div>
  );
};

export default VoyagePerformanceSummary;
